import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../lib/AuthContext";
import { api, API } from "../lib/api";
import { Navigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Search, Trash2, Plus, FileText, Files, ArrowLeft } from "lucide-react";
import { NUTRIENTS } from "./Nuna";
import { useI18n } from "../lib/LanguageContext";
import { localizeContent } from "../lib/translations";

const FREQ = [
  { v: "daily", l: "Setiap hari" },
  { v: "4-6_week", l: "4-6× / minggu" },
  { v: "1-3_week", l: "1-3× / minggu" },
  { v: "1-3_month", l: "1-3× / bulan" },
  { v: "rarely", l: "Jarang" },
  { v: "never", l: "Tidak pernah" },
];

export default function FFQPage() {
  const { lang } = useI18n();
  const tr = (value) => localizeContent(value, lang);
  const { user, loading, refresh } = useAuth();
  const [subject, setSubject] = useState({ name: "", age: 25, sex: "male", weight: 60, height: 165, activity: "sedang" });
  const [standard, setStandard] = useState("male_adult");
  const [query, setQuery] = useState(""); const [foods, setFoods] = useState([]);
  const [items, setItems] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => { if (user && !subject.name) setSubject(s => ({...s, name: user.full_name})); }, [user]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams(); if (query) params.set("q", query);
      api.get(`/foods?${params}&limit=30`).then(r => setFoods(r.data));
    }, 250); return () => clearTimeout(t);
  }, [query]);

  const addItem = (f) => {
    if (items.find(x => x.food_id === f.id)) return toast.error(tr("Sudah ada"));
    setItems(prev => [...prev, { food_id: f.id, name: f.name, grams_per_portion: 100, frequency: "1-3_week", portions_per_frequency: 1 }]);
  };

  const update = (i, patch) => setItems(prev => prev.map((it, idx) => idx===i ? {...it, ...patch} : it));
  const remove = (i) => setItems(prev => prev.filter((_,idx)=>idx!==i));

  const submit = async () => {
    if (items.length === 0) return toast.error(tr("Tambahkan minimal 1 makanan"));
    try {
      const r = await api.post("/nuna/ffq", { title: `FFQ ${new Date().toLocaleDateString("id-ID")}`, subject, standard, items });
      setResult(r.data); await refresh(); toast.success(tr("FFQ berhasil dihitung"));
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const downloadDocx = async () => {
    if (!result) return;
    const token = localStorage.getItem("gidza_token");
    const resp = await fetch(`${API}/nuna/docx/${result.id}`, { headers: { Authorization: `Bearer ${token}` }});
    const blob = await resp.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `FFQ_${result.subject.name}.docx`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-teal border-t-transparent rounded-full animate-spin"/></div>;
  if (!user) return <Navigate to="/masuk" replace/>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="ffq-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/nuna" className="text-teal hover:underline flex items-center text-sm"><ArrowLeft className="w-4 h-4 mr-1"/>{tr("Kembali ke NUNA")}</Link>
      </div>
      <div className="mb-6">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-teal mb-1">{tr("Food Frequency Questionnaire")}</div>
        <h1 className="font-display text-3xl md:text-4xl font-medium">{tr("FFQ / Semi-FFQ Assessment")}</h1>
        <p className="text-muted-foreground mt-1 text-sm">{tr("Kumpulkan intake makanan pasien / responden berdasarkan frekuensi konsumsi.")}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="dash-panel p-5 md:col-span-1" data-testid="ffq-subject">
          <h3 className="font-display font-medium mb-4">{tr("Data Responden")}</h3>
          <div className="space-y-3">
            <Field label={tr("Nama")}><Input value={subject.name} onChange={(e)=>setSubject({...subject, name:e.target.value})}/></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr("Usia")}><Input type="number" value={subject.age} onChange={(e)=>setSubject({...subject, age:+e.target.value})}/></Field>
              <Field label={tr("Jenis")}><select value={subject.sex} onChange={(e)=>setSubject({...subject, sex:e.target.value})} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-white"><option value="male">{tr("Pria")}</option><option value="female">{tr("Wanita")}</option></select></Field>
              <Field label={tr("BB (kg)")}><Input type="number" value={subject.weight} onChange={(e)=>setSubject({...subject, weight:+e.target.value})}/></Field>
              <Field label={tr("TB (cm)")}><Input type="number" value={subject.height} onChange={(e)=>setSubject({...subject, height:+e.target.value})}/></Field>
            </div>
            <Field label={tr("Standar AKG")}>
              <select value={standard} onChange={(e)=>setStandard(e.target.value)} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-white">
                <option value="male_adult">{tr("Pria Dewasa (AKG Indonesia)")}</option><option value="female_adult">{tr("Wanita Dewasa (AKG Indonesia)")}</option>
                <option value="male_teen">{tr("Pria Remaja")}</option><option value="female_teen">{tr("Wanita Remaja")}</option>
              </select>
            </Field>
          </div>
        </Card>

        <Card className="dash-panel p-5 md:col-span-2" data-testid="ffq-items">
          <div className="flex items-center gap-2 mb-3"><Search className="w-4 h-4 text-teal"/><Input placeholder={tr("Cari makanan...")} value={query} onChange={(e)=>setQuery(e.target.value)} data-testid="ffq-search"/></div>
          <div className="max-h-40 overflow-y-auto no-scrollbar border border-teal/10 rounded mb-4">
            {foods.map(f => (
              <button key={f.id} onClick={()=>addItem(f)} className="w-full text-left py-2 px-3 hover:bg-teal-soft/40 flex justify-between text-sm border-b border-teal/5" data-testid={`ffq-food-${f.id}`}>
                <span>{f.name}</span><Plus className="w-4 h-4 text-teal"/>
              </button>
            ))}
          </div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {items.length===0 ? <p className="text-sm text-muted-foreground text-center py-6">{tr("Cari & tambahkan makanan ke daftar FFQ")}</p> :
              items.map((it, i) => (
                <div key={i} className="border border-teal/10 rounded-md p-3 grid grid-cols-1 md:grid-cols-5 gap-2 items-center" data-testid={`ffq-item-${i}`}>
                  <div className="md:col-span-2 text-sm font-medium">{it.name}</div>
                  <select value={it.frequency} onChange={(e)=>update(i, {frequency: e.target.value})} className="border border-input rounded-md h-9 px-2 text-xs bg-white" data-testid={`ffq-freq-${i}`}>
                    {FREQ.map(f => <option key={f.v} value={f.v}>{tr(f.l)}</option>)}
                  </select>
                  <div className="flex items-center gap-1"><Input type="number" className="h-9" value={it.grams_per_portion} onChange={(e)=>update(i, {grams_per_portion:+e.target.value})}/><span className="text-xs">{tr("g/porsi")}</span></div>
                  <div className="flex items-center gap-1"><Input type="number" className="h-9 w-16" value={it.portions_per_frequency} onChange={(e)=>update(i, {portions_per_frequency:+e.target.value})}/><span className="text-xs">{tr("porsi")}</span>
                    <button onClick={()=>remove(i)} className="text-destructive ml-auto"><Trash2 className="w-4 h-4"/></button>
                  </div>
                </div>
              ))
            }
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            <Button onClick={submit} className="rounded-md bg-teal hover:bg-teal/90 text-white" data-testid="ffq-submit">{tr("Hitung Intake Harian Rata-rata")}</Button>
            {result && <Button variant="outline" onClick={downloadDocx} className="rounded-md" data-testid="ffq-download-docx"><Files className="w-4 h-4 mr-1"/> Word</Button>}
          </div>
        </Card>
      </div>

      {result && (
        <Card className="dash-panel p-5 mt-6" data-testid="ffq-result">
          <h3 className="font-display font-medium mb-3">{tr("Rata-rata Intake Harian vs AKG")}</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {NUTRIENTS.map(n => (
              <div key={n.k} className="border border-teal/10 rounded-md p-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{tr(n.l)}</div>
                <div className="font-display text-lg font-medium">{result.totals[n.k].toFixed(1)} <span className="text-xs text-muted-foreground">{n.u}</span></div>
                <div className="nutrient-bar mt-1"><span style={{width:`${Math.min(150, result.percentages[n.k])}%`}}/></div>
                <div className={`text-xs mt-1 font-semibold ${result.percentages[n.k]>=80 && result.percentages[n.k]<=120 ? "text-teal":"text-orange-500"}`}>{result.percentages[n.k]}% AKG</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Field({ label, children }) { return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>; }
